import { useEffect, useState } from 'react'

import { Box, useTheme } from '@mui/material'
import request from 'api'
import { Loading } from 'components/ui'
import dayjs from 'dayjs'
import { useDateTime, useFamily, useMappedTranslation } from 'hooks'
import { Paper } from 'oneui'
import { SigninDeviceProps } from 'pages/dashboard/home/home'
import { MethodType, Recovery, fetchRecoveries, fetchSecurityQuestionsCount } from 'pages/dashboard/security/recovery/api'
import useHidingMfaQuery from 'queries/use-hiding-mfa-query'
import { setIsNoPassword, useAppDispatch, useAppSelector } from 'store'
import { SummaryPaperStyle } from 'styles/theme/theme'
import { maskingPhoneNumber } from 'utils/common'
import { ROUTES } from 'utils/constants'
import { getCountryCode, getInternationalPhoneNumber } from 'utils/libphonenumber'

import CombinedCard from './combined-card'
import SeparatedCard from './separated-card'
import { getDeviceIcon } from '../../devices/deviceMap'
import { getSecurityIcon } from '../assets/images/securityIcon'

type PasswordInfoResponse = {
  recommendResetPeriod: number
  twoFactorVerification: boolean
  changedTimestamp?: number
}

type PasswordStatusResponse = {
  passwordCompromised: boolean
}

type PhonenumberResponse = {
  isUnverifiedPhone: boolean
  countryCode: string
  phoneNumber: string
}

type ActivityResponse = {
  type: 'LOGIN_SUCCESS' | 'CHANGE_PROFILE' | 'CHANGE_PASSWORD' | 'CHANGE_RECOVERY_INFO' | 'REGISTER_TFA'
  ip: string
  region: string
  createdTimestamp: number
  details?: {
    channel: string
    modelName: string
    deviceType?: string
  }
}

type Activity = {
  icon: string
  description: string
}

type SecurityEvent = {
  type:
    | 'NO_PASSWORD'
    | 'NO_PHONE'
    | 'NO_EMAIL'
    | 'UNVERIFIED_PHONE'
    | 'OLD_PASSWORD'
    | 'NO_SECURITY_QUESTIONS'
    | 'PASSWORD_COMPROMISED'
  title?: string
  description?: string
  link?: string
}

const SecuritySummary = ({ signinDevices, isSigninDevicesLoading }: SigninDeviceProps) => {
  const confirmPasswordKey = useAppSelector((state) => state.confirmPassword.key)
  const { data: isMfaHidden, isLoading: isMfaHiddenLoading } = useHidingMfaQuery(confirmPasswordKey)
  const theme = useTheme()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [securityEvent, setSecurityEvent] = useState<SecurityEvent>()
  const [activity, setActivity] = useState<Activity>({
    icon: getSecurityIcon('IconUpdated', theme.palette.mode),
    description: ''
  })
  const [passwordChangeDate, setPasswordChangeDate] = useState<string>('')
  const [tsvEnabled, setTsvEnabled] = useState<boolean>(false)

  const t = useMappedTranslation()
  const dispatch = useAppDispatch()
  const dateTime = useDateTime()
  const { family, isInitializing: isFamilyInitializing } = useFamily()

  const getPasswordInfoApi = () => {
    return request
      .get('/v3/security/password', { headers: { 'x-confirm-key': confirmPasswordKey } })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Failed getPasswordInfoApi')
        }

        return response.data as PasswordInfoResponse
      })
      .catch(() => {
        return {
          recommendResetPeriod: 180,
          twoFactorVerification: false
        } as PasswordInfoResponse
      })
  }

  const getActivityApi = () => {
    return request
      .get('/v3/dashboard/activity/latest?limit=1', { headers: { 'x-confirm-key': confirmPasswordKey } })
      .then((response) => {
        if (response.status !== 200 || response.data?.length === undefined) {
          throw new Error('Failed getActivityApi')
        }

        return response.data.at(0) as ActivityResponse
      })
      .catch(() => {
        return {
          type: 'LOGIN_SUCCESS',
          ip: '',
          region: '',
          createdTimestamp: 0
        } as ActivityResponse
      })
  }

  const fetchPhoneNumberDetail = () => {
    return request
      .get('/v1/security/authentication/sub/configuration/1')
      .then((res) => {
        if (res.status === 200) {
          if (res.data[0].authenticateValueText04 === 'unverified') {
            return {
              isUnverifiedPhone: true,
              countryCode: getCountryCode(res.data[0].authenticateValueText02),
              phoneNumber: res.data[0].authenticateValueText01
            } as PhonenumberResponse
          }
        }
        return {
          isUnverifiedPhone: false,
          countryCode: '',
          phoneNumber: ''
        } as PhonenumberResponse
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const getPasswordStatus = () => {
    return request
      .get('/credentials/passwords')
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Failed getPasswordStatusApi')
        }
        return response.data as PasswordStatusResponse
      })
      .catch(() => {
        return {
          passwordCompromised: false
        } as PasswordStatusResponse
      })
  }

  useEffect(() => {
    if (isMfaHiddenLoading || isSigninDevicesLoading || isFamilyInitializing) {
      return
    }
    Promise.all([
      getPasswordInfoApi(),
      fetchRecoveries()
        .then((res) => {
          return res.data
        })
        .catch(() => {
          return [] as Recovery[]
        }),
      fetchPhoneNumberDetail(),
      getActivityApi(),
      fetchSecurityQuestionsCount()
        .then((res) => {
          return res.data ? res.data : 0
        })
        .catch(() => {}),
      getPasswordStatus()
    ])
      .then(([passwordInfo, recoveries, phoneNumberResponse, activity, questionsCount, passwordStatusResponse]) => {
        const emailRecoveries = recoveries.filter((recovery) => recovery.methodType === MethodType.EMAIL)
        setTsvEnabled(passwordInfo.twoFactorVerification)

        if (passwordStatusResponse.passwordCompromised) {
          setSecurityEvent({
            type: 'PASSWORD_COMPROMISED',
            title: t('iam.01.Common.Title.Password.compromised'),
            description: t('iam.01.Common.Txt.We.found.that.your.account.password.was.exposed'),
            link: family.spcEnabled ? ROUTES.RESET_PWD_CONFIRM_ORGANIZER : ROUTES.RESET_PWD
          })
        } else if (passwordInfo?.changedTimestamp === undefined) {
          dispatch(setIsNoPassword(true))
          setSecurityEvent({
            type: 'NO_PASSWORD',
            title: t('security.event.noPassword.title'),
            description: t('security.event.noPassword.description'),
            link: '/dashboard/register-password'
          })
        } else {
          if (
            !isMfaHiddenLoading &&
            !isMfaHidden &&
            !passwordInfo.twoFactorVerification &&
            signinDevices.filter((device) => device.type === 'PHONE').length > 0
          ) {
            setSecurityEvent({
              type: 'NO_PHONE',
              title: t('security.event.noPhone.title'),
              description: t('security.event.noPhone.description'),
              link: '/dashboard/security/phone'
            })
          } else if (emailRecoveries.length === 0) {
            setSecurityEvent({
              type: 'NO_EMAIL',
              title: t('security.event.noEmail.title'),
              description: t('security.event.noEmail.description'),
              link: '/dashboard/security/recovery/email'
            })
          } else if (questionsCount === 0) {
            setSecurityEvent({
              type: 'NO_SECURITY_QUESTIONS',
              title: t('iam.01.Common.Title.Security.question.expired'),
              description: t('iam.01.Common.Txt.You.cant.use.your.security.question.as.a.recovery.method'),
              link: '/dashboard/security/recovery/security-question'
            })
          } else if (phoneNumberResponse?.isUnverifiedPhone && phoneNumberResponse.phoneNumber !== '') {
            setSecurityEvent({
              type: 'UNVERIFIED_PHONE',
              title: t('iam.01.Common.Title.Phone.number.not.verified'),
              description: t('iam.01.Common.Txt.Verify.your.phone.number.to.finish.setting.up.your.account', {
                0: maskingPhoneNumber(
                  getInternationalPhoneNumber(phoneNumberResponse.countryCode, phoneNumberResponse.phoneNumber)
                )
              }),
              link: '/dashboard/security/phone'
            })
          } else {
            dispatch(setIsNoPassword(false))
            const today = dayjs()
            const passwordExpiredDate: dayjs.Dayjs = dayjs
              .unix(passwordInfo.changedTimestamp)
              .add(passwordInfo.recommendResetPeriod, 'day')

            if (passwordExpiredDate.isBefore(today)) {
              setSecurityEvent({
                type: 'OLD_PASSWORD',
                title: t('security.event.oldPassword.title'),
                description: t('security.event.oldPassword.description'),
                link: '/dashboard/security/reset-password'
              })
            }
          }
        }

        switch (activity.type) {
          case 'LOGIN_SUCCESS':
            let icon
            if (activity.details?.channel === 'DEVICE' && activity.details.deviceType) {
              icon = getDeviceIcon(activity.details.deviceType, theme.palette.mode)
            } else {
              icon = getSecurityIcon('IconSignIn', theme.palette.mode)
            }
            setActivity({
              icon: icon,
              description: t('security.recentActivity.signIn', { 0: activity.details?.modelName })
            })
            break
          case 'REGISTER_TFA':
            setActivity({
              icon: getSecurityIcon('Icon2sv', theme.palette.mode),
              description: t('security.recentActivity.2fa.added')
            })
            break
          case 'CHANGE_PROFILE':
            setActivity({
              icon: getSecurityIcon('IconUpdated', theme.palette.mode),
              description: t('security.recentActivity.changeProfile')
            })
            break
          case 'CHANGE_PASSWORD':
            setActivity({
              icon: getSecurityIcon('IconUpdated', theme.palette.mode),
              description: t('security.recentActivity.changePassword')
            })
            break
          case 'CHANGE_RECOVERY_INFO':
            setActivity({
              icon: getSecurityIcon('IconUpdated', theme.palette.mode),
              description: t('security.recentActivity.changeRecoveryInfo')
            })
            break
        }

        if (passwordInfo.changedTimestamp) {
          let _passwordChangedDate

          const nowDate = dayjs()
          const changedDate = dayjs.unix(passwordInfo.changedTimestamp)
          const diffDays = nowDate.diff(changedDate, 'day')
          const diffHours = nowDate.diff(changedDate, 'hour')
          const diffMinutes = nowDate.diff(changedDate, 'minute')

          if (diffDays > 7) {
            _passwordChangedDate = t('security.changedPassword.date', {
              0: dateTime(passwordInfo.changedTimestamp, { format: 'withoutTime' })
            })
          } else if (diffDays) {
            _passwordChangedDate = t('security.changedPassword.daysAgo', { 0: diffDays })
          } else if (diffHours) {
            _passwordChangedDate = t('security.changedPassword.time.hoursAgo', { 0: diffHours })
          } else if (0 < diffMinutes && diffMinutes < 30) {
            _passwordChangedDate = t('security.changedPassword.time.minAgo', { 0: diffMinutes })
          } else {
            _passwordChangedDate = t('security.changedPassword.latest')
          }

          setPasswordChangeDate(_passwordChangedDate)
        }
      })
      .finally(() => setIsLoading(false))
  }, [isMfaHidden, isMfaHiddenLoading, isSigninDevicesLoading, isFamilyInitializing])

  return (
    <Box>
      {isLoading || isMfaHiddenLoading ? (
        <Paper
          sx={{
            height: '400px',
            ...SummaryPaperStyle
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Loading />
          </Box>
        </Paper>
      ) : securityEvent === undefined ? (
        <CombinedCard activity={activity} passwordChangeDate={passwordChangeDate} tsvEnabled={tsvEnabled && !isMfaHidden} />
      ) : (
        <SeparatedCard activity={activity} securityEvent={securityEvent} />
      )}
    </Box>
  )
}

export default SecuritySummary
